/**
 * TravelDistanceEditor — the Movement Goal panel's travel-distance
 * (body-height ratio) controls.
 *
 * Owner's flow (docs/coach_input/OWNER_DECISIONS_2026-10-09_travel.md):
 * select the landmark (done by the parent panel, outside this component) ->
 * pick the phase (the shared `target_phase` control every goal type already
 * uses, also outside this component) -> **A. "Use my demo"** (the DEFAULT)
 * or **B. "Set it myself"**. The coach must be able to see what the system
 * measured from the demo next to what they want.
 *
 * Built against `docs/coach_input/TRAVEL_PR_A_CONTRACT.md` — field names on
 * `TravelTarget` are byte-identical to the backend Pydantic model. Renders
 * NO `body_threshold`/`BodySegmentSelector` picker and NO gate toggle — a
 * travel target cannot be a gate (the backend 422s the pairing); this
 * component's own container never renders that control.
 */

import { useEffect, useRef, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { TravelTarget } from '@/types/trajectory';
import {
  previewTravelTarget,
  type TravelPreviewResponse,
} from '@/services/templateApi';
import {
  travelPanelSubtitle,
  TRAVEL_PANEL_WHY,
  TRAVEL_PANEL_HOW,
  TRAVEL_SOURCE_DEMO_LABEL,
  TRAVEL_SOURCE_DEMO_WHY,
  TRAVEL_SOURCE_TYPED_LABEL,
  TRAVEL_SOURCE_TYPED_WHY,
  TRAVEL_SOURCE_TYPED_HOW,
  demoMeasuredLine,
  TRAVEL_DEMO_MEASURED_HOW,
  travelToleranceFloorStatement,
  computeTravelToleranceFloor,
  TRAVEL_IMPORTANCE_HINT,
  TRAVEL_IMPORTANCE_WHY,
  TRAVEL_ABOVE_DEMO_NOTICE,
  TRAVEL_ABOVE_DEMO_HOW,
  TRAVEL_WINDOW_LINE,
  TRAVEL_WINDOW_HOW,
  TRAVEL_SMALL_MOVEMENT_NOTE,
  TRAVEL_SMALL_MOVEMENT_WHY,
  TRAVEL_SMALL_MOVEMENT_HOW,
} from '@/utils/travelDistanceCopy';

// How long to wait after a landmark/phase change before asking the backend
// to measure the demo again. Not a keystroke debounce (landmark/phase are
// discrete picks, not typed text) — this coalesces React's own render
// churn when several props change in the same tick.
const PREVIEW_DEBOUNCE_MS = 250;

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: TravelPreviewResponse }
  | { status: 'error'; message: string };

export interface TravelDistanceEditorProps {
  value: TravelTarget | undefined;
  onChange: (value: TravelTarget) => void;
  templateId?: string;
  /** The tracked landmark (anatomical or lead_/trail_ functional name). */
  landmark?: string;
  /** Plain-English label for the landmark, for copy — never a raw
   * identifier ("Right Wrist", not "right_wrist"). */
  landmarkLabel?: string;
  /** Phase label to scope the measurement to. `null`/undefined = whole clip. */
  targetPhase?: string | null;
  /**
   * Bubbles the demo's own travel path up to whoever renders the authoring
   * canvas (contract §2.1 / `backlog/travel-preview-endpoint-should-return-
   * demo-path-points.md`), so it can be drawn as the demo-path preview
   * while this editor is open. Called with the preview's
   * `trajectory_points` on a successful measurement, and with `null`
   * whenever there is nothing to draw: on abstention, on error, and
   * immediately when the landmark/phase selection changes (the points from
   * the PREVIOUS selection no longer apply) — never left stale.
   */
  onPreviewTrajectoryChange?: (points: Array<[number, number, number]> | null) => void;
  className?: string;
}

/**
 * Which radio is selected. `target_source` is the authoritative signal (the
 * contract's own purpose for the field: "restore which control was
 * selected when reopening the goal") — checked FIRST so clicking "B" shows
 * the input immediately, even before the coach has typed a number into it
 * (at that point `value_body_heights` is still unset). Falls back to
 * `value_body_heights != null` for a config that has a typed value but no
 * `target_source` (e.g. written by a future PR-C path).
 */
function isTypedMode(value: TravelTarget | undefined): boolean {
  if (!value) return false;
  if (value.target_source === 'coach') return true;
  if (value.target_source === 'demo' || value.target_source === 'demo_multiple') return false;
  return value.value_body_heights != null;
}

export default function TravelDistanceEditor({
  value,
  onChange,
  templateId,
  landmark,
  landmarkLabel,
  targetPhase,
  onPreviewTrajectoryChange,
  className,
}: TravelDistanceEditorProps) {
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const requestIdRef = useRef(0);

  // Latest callback in a ref so the effects below never need it in their
  // dependency array (it is commonly an inline/unmemoized function from the
  // parent -- adding it to deps would re-fire the debounced preview fetch
  // on every parent render, not just on a real landmark/phase change).
  const onPreviewTrajectoryChangeRef = useRef(onPreviewTrajectoryChange);
  onPreviewTrajectoryChangeRef.current = onPreviewTrajectoryChange;

  const typedMode = isTypedMode(value);

  // Fetch the demo's measured ratio whenever the landmark/phase/template
  // selection changes (contract §2: "whenever the landmark or phase
  // selection changes" — never on every keystroke, so this effect does NOT
  // depend on the typed number field).
  useEffect(() => {
    // The previous selection's demo path no longer applies to the new
    // landmark/phase -- clear it immediately rather than leaving a stale
    // path on screen while the new preview loads.
    onPreviewTrajectoryChangeRef.current?.(null);

    if (!templateId || !landmark) {
      setPreview({ status: 'idle' });
      return;
    }

    const myRequestId = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      setPreview({ status: 'loading' });
      previewTravelTarget(templateId, { landmark, target_phase: targetPhase ?? null })
        .then((data) => {
          if (requestIdRef.current !== myRequestId) return;
          setPreview({ status: 'ok', data });
          if (data.status === 'computed' && data.ratio != null) {
            onChange({ ...value, demo_value_body_heights: data.ratio });
          }
          onPreviewTrajectoryChangeRef.current?.(
            data.status === 'computed' ? data.trajectory_points ?? null : null,
          );
        })
        .catch(() => {
          if (requestIdRef.current !== myRequestId) return;
          setPreview({
            status: 'error',
            message: "We couldn't measure your demo just now. Try again in a moment.",
          });
          onPreviewTrajectoryChangeRef.current?.(null);
        });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
    }, PREVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
    // `value` is intentionally excluded — this effect reacts to WHERE we're
    // measuring (landmark/phase/template), not to what the coach has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, landmark, targetPhase]);

  // Clear the demo-path preview when this editor unmounts (e.g. the coach
  // switches away from the travel-target mode entirely) — otherwise the
  // last-drawn path would stay stuck on the canvas after the control that
  // produced it is gone.
  useEffect(() => {
    return () => onPreviewTrajectoryChangeRef.current?.(null);
  }, []);

  const demoRatio = preview.status === 'ok' && preview.data.status === 'computed' ? preview.data.ratio : null;

  const handleSelectDemo = () => {
    onChange({
      ...value,
      target_source: 'demo',
      value_body_heights: null,
      demo_multiple: null,
    });
  };

  const handleSelectTyped = () => {
    onChange({
      ...value,
      target_source: 'coach',
      value_body_heights: value?.value_body_heights ?? undefined,
      demo_multiple: null,
    });
  };

  const handleTypedValueChange = (raw: string) => {
    const parsed = raw.trim() === '' ? null : Number(raw);
    onChange({
      ...value,
      target_source: 'coach',
      value_body_heights: parsed !== null && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
      demo_multiple: null,
    });
  };

  const handleToleranceChange = (raw: string) => {
    const parsed = raw.trim() === '' ? null : Number(raw);
    onChange({
      ...value,
      tolerance_body_heights: parsed !== null && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    });
  };

  // The best number we have for "the bar" right now, for the floor display
  // and the above-demo notice — the coach's typed value when in B, else the
  // demo's measured ratio when known.
  const effectiveTarget = typedMode ? value?.value_body_heights ?? null : demoRatio;
  const toleranceFloor = computeTravelToleranceFloor(effectiveTarget);

  const aboveDemo =
    typedMode &&
    value?.value_body_heights != null &&
    demoRatio != null &&
    value.value_body_heights > demoRatio;

  return (
    <div
      className={cn('space-y-3 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3', className)}
      data-testid="travel-distance-editor"
    >
      <div>
        <p className="text-sm font-medium text-text-primary">{travelPanelSubtitle(landmarkLabel)}</p>
        <p className="mt-1 text-xs text-text-secondary">{TRAVEL_PANEL_WHY}</p>
        <p className="mt-0.5 text-xs text-text-muted">{TRAVEL_PANEL_HOW}</p>
      </div>

      {/* A / B source choice */}
      <div className="space-y-2" role="radiogroup" aria-label="How to set the travel target">
        <label
          className={cn(
            'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-150',
            !typedMode
              ? 'bg-success/10 border-success/40'
              : 'bg-dark-card border-border-subtle hover:border-border-strong',
          )}
        >
          <input
            type="radio"
            name="travel-target-source"
            checked={!typedMode}
            onChange={handleSelectDemo}
            className="h-4 w-4 mt-0.5 text-success focus:ring-success"
            data-testid="travel-source-demo-radio"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary">{TRAVEL_SOURCE_DEMO_LABEL}</div>
            <div className="text-xs text-text-secondary mt-0.5">{TRAVEL_SOURCE_DEMO_WHY}</div>

            {!typedMode && (
              <div className="mt-2 text-xs" data-testid="travel-demo-measurement">
                {preview.status === 'loading' && (
                  <span className="inline-flex items-center gap-1.5 text-text-muted">
                    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Measuring your demo…
                  </span>
                )}
                {preview.status === 'ok' && preview.data.status === 'computed' && (
                  <>
                    <p className="font-medium text-success">{demoMeasuredLine(preview.data.ratio)}</p>
                    <p className="text-text-muted">{TRAVEL_DEMO_MEASURED_HOW}</p>
                  </>
                )}
                {preview.status === 'ok' && preview.data.status === 'abstained' && (
                  <p className="text-warning" data-testid="travel-demo-abstained-reason">
                    {preview.data.reason}
                  </p>
                )}
                {preview.status === 'error' && (
                  <p className="text-danger">{preview.message}</p>
                )}
                {preview.status === 'idle' && !templateId && (
                  <p className="text-text-muted">Save this template first to measure your demo.</p>
                )}
                {preview.status === 'idle' && templateId && !landmark && (
                  <p className="text-text-muted">Pick a point to track above, then we'll measure it.</p>
                )}
              </div>
            )}
          </div>
        </label>

        <label
          className={cn(
            'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-150',
            typedMode
              ? 'bg-success/10 border-success/40'
              : 'bg-dark-card border-border-subtle hover:border-border-strong',
          )}
        >
          <input
            type="radio"
            name="travel-target-source"
            checked={typedMode}
            onChange={handleSelectTyped}
            className="h-4 w-4 mt-0.5 text-success focus:ring-success"
            data-testid="travel-source-typed-radio"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary">{TRAVEL_SOURCE_TYPED_LABEL}</div>
            <div className="text-xs text-text-secondary mt-0.5">{TRAVEL_SOURCE_TYPED_WHY}</div>
            <div className="text-xs text-text-muted mt-0.5">{TRAVEL_SOURCE_TYPED_HOW}</div>

            {typedMode && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={value?.value_body_heights ?? ''}
                  onChange={(e) => handleTypedValueChange(e.target.value)}
                  className="w-24 px-2 py-1.5 border border-border-subtle rounded text-sm text-center bg-dark-card text-text-primary"
                  data-testid="travel-typed-value-input"
                  aria-label="Target, in body heights"
                />
                <span className="text-xs text-text-secondary">of how tall they look</span>
                {demoRatio != null && (
                  <span className="text-xs text-text-muted" data-testid="travel-demo-beside-typed">
                    (your demo: {demoRatio.toFixed(2)})
                  </span>
                )}
              </div>
            )}
          </div>
        </label>
      </div>

      {/* #7 — above-demo notice */}
      {aboveDemo && (
        <div
          className="flex items-start gap-2 p-2 bg-info/10 border border-info/30 rounded-lg text-xs text-info"
          data-testid="travel-above-demo-notice"
        >
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            <strong>{TRAVEL_ABOVE_DEMO_NOTICE}</strong> {TRAVEL_ABOVE_DEMO_HOW}
          </span>
        </div>
      )}

      {/* #6 — slack / tolerance, with the enforced-floor statement */}
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-text-secondary">
          How much under the target still counts as close enough? (optional)
        </label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          inputMode="decimal"
          value={value?.tolerance_body_heights ?? ''}
          onChange={(e) => handleToleranceChange(e.target.value)}
          placeholder="Leave blank to use the standard amount"
          className="w-full px-3 py-2 border border-border-subtle rounded text-sm bg-dark-card text-text-primary"
          data-testid="travel-tolerance-input"
        />
        {toleranceFloor !== null && (
          <p className="text-xs text-text-muted" data-testid="travel-tolerance-floor-statement">
            {travelToleranceFloorStatement(toleranceFloor)}
          </p>
        )}
      </div>

      {/* #6a — importance hint */}
      <div className="flex items-start gap-2 p-2 bg-dark-card rounded text-xs text-text-secondary">
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-text-muted" />
        <span>
          <strong>{TRAVEL_IMPORTANCE_HINT}</strong> {TRAVEL_IMPORTANCE_WHY}
        </span>
      </div>

      {/* #9 — window line, only when no phase is selected */}
      {!targetPhase && (
        <div
          className="flex items-start gap-2 p-2 bg-dark-card rounded text-xs text-text-secondary"
          data-testid="travel-window-line"
        >
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-text-muted" />
          <span>
            {TRAVEL_WINDOW_LINE} {TRAVEL_WINDOW_HOW}
          </span>
        </div>
      )}

      {/* Item 4 — the honest guidance (plan §4.5, the price of the enforced
          floor): always shown, not conditioned on a score existing. */}
      <div
        className="flex items-start gap-2 p-2 bg-dark-card rounded text-xs text-text-secondary"
        data-testid="travel-small-movement-note"
      >
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-text-muted" />
        <span>
          <strong>{TRAVEL_SMALL_MOVEMENT_NOTE}</strong> {TRAVEL_SMALL_MOVEMENT_WHY}{' '}
          {TRAVEL_SMALL_MOVEMENT_HOW}
        </span>
      </div>
    </div>
  );
}
